<div class="container">

  <header class="page-heading">
    <h1>HRA Exemption Calculator</h1>
    <p>Calculate your House Rent Allowance (HRA) tax exemption under Section 10(13A) for the Old Tax Regime.</p>
  </header>

  <div class="card mb-lg">
    <div class="section-title">HRA Exemption Details</div>

    <div class="field-group mb-md">
      <label class="field-label" for="city">City of Residence</label>
      <div class="input-shell">
        <select id="city" [ngModel]="city" (ngModelChange)="setCity($event)">
          <optgroup label="Metro (50% of Basic Salary)">
            @for (c of metroCities; track c) {
              <option [value]="c">{{ c }}</option>
            }
          </optgroup>
          <optgroup label="Non-Metro (40% of Basic Salary)">
            @for (c of nonMetroCities; track c) {
              <option [value]="c">{{ c }}</option>
            }
          </optgroup>
        </select>
      </div>
      <div class="field-hint">
        From FY 2026-27, Delhi, Mumbai, Kolkata, Chennai, Bangalore, Pune, Hyderabad and Ahmedabad
        qualify as metro cities for HRA exemption under Section 10(13A). All other cities use the 40% limit.
      </div>
    </div>

    <div class="field-row-2">
      <div class="field-group" [class.has-error]="hasError('monthlyBasicSalary')">
        <label class="field-label" for="monthlyBasicSalary">Monthly Basic + DA</label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="monthlyBasicSalary" type="number"
            [(ngModel)]="monthlyBasicSalary"
            [min]="C.basicSalary.min" [max]="C.basicSalary.max" [step]="C.basicSalary.step"
            (ngModelChange)="onFieldChange('monthlyBasicSalary')"
            (blur)="onFieldBlur('monthlyBasicSalary')"
            placeholder="e.g. 50,000"
            autocomplete="off"/>
        </div>
        @if (hasError('monthlyBasicSalary')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('monthlyBasicSalary') }}
          </div>
        }
      </div>

      <div class="field-group" [class.has-error]="hasError('monthlyHraReceived')">
        <label class="field-label" for="monthlyHraReceived">Monthly HRA Received</label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="monthlyHraReceived" type="number"
            [(ngModel)]="monthlyHraReceived"
            [min]="C.hraReceived.min" [max]="C.hraReceived.max" [step]="C.hraReceived.step"
            (ngModelChange)="onFieldChange('monthlyHraReceived')"
            (blur)="onFieldBlur('monthlyHraReceived')"
            placeholder="e.g. 20,000"
            autocomplete="off"/>
        </div>
        @if (hasError('monthlyHraReceived')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('monthlyHraReceived') }}
          </div>
        }
      </div>
    </div>

    <div class="field-group" [class.has-error]="hasError('monthlyRentPaid')">
      <label class="field-label" for="monthlyRentPaid">Monthly Rent Paid</label>
      <div class="input-shell prefix">
        <span class="affix">₹</span>
        <input id="monthlyRentPaid" type="number"
          [(ngModel)]="monthlyRentPaid"
          [min]="C.rentPaid.min" [max]="C.rentPaid.max" [step]="C.rentPaid.step"
          (ngModelChange)="onFieldChange('monthlyRentPaid')"
          (blur)="onFieldBlur('monthlyRentPaid')"
          placeholder="e.g. 18,000"
          autocomplete="off"/>
      </div>
      @if (hasError('monthlyRentPaid')) {
        <div class="field-error">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          {{ fieldError('monthlyRentPaid') }}
        </div>
      }
    </div>

    <div class="action-row">
      <button type="button" class="btn btn-primary" (click)="calculate()" [class.btn-disabled]="showFormErrors && !isFormValid">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
        Calculate
      </button>
      @if (calculated) {
        <button type="button" class="btn btn-secondary" (click)="reset()">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
          Reset
        </button>
      }
      @if (showFormErrors && !isFormValid) {
        <span class="form-error-hint">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          Fix the errors above to calculate
        </span>
      }
    </div>
  </div>

  @if (calculated && result) {
    <div class="summary-grid mb-md">
      <div class="summary-card c-green">
        <div class="sc-icon"></div>
        <div class="sc-label">Monthly HRA Exemption</div>
        <div class="sc-value">{{ fmt.currency(result.exemptHra) }}</div>
        <div class="sc-sub">annual: {{ fmt.currency(result.annualExemptHra) }}</div>
      </div>
      <div class="summary-card c-red">
        <div class="sc-icon"></div>
        <div class="sc-label">Monthly Taxable HRA</div>
        <div class="sc-value">{{ fmt.currency(result.taxableHra) }}</div>
        <div class="sc-sub">annual: {{ fmt.currency(result.annualTaxableHra) }}</div>
      </div>
      <div class="summary-card c-blue">
        <div class="sc-icon"></div>
        <div class="sc-label">Limiting Factor</div>
        <div class="sc-value" style="font-size: 1rem;">
          @if (result.limitingFactor === 'actualHra') { Actual HRA Received }
          @else if (result.limitingFactor === 'rentMinusTenPercent') { Rent &minus; 10% of Salary }
          @else { {{ isMetro ? '50%' : '40%' }} of Basic Salary }
        </div>
      </div>
    </div>

    <div class="card charts-card mb-md">
      <div class="section-title">HRA Breakdown</div>
      <div class="chart-panel">
        <div class="chart-canvas-wrap chart-canvas-pie">
          <canvas #breakdownRef></canvas>
        </div>
      </div>
    </div>

    <div class="card mb-md">
      <div class="section-title">Exemption Calculation (per Section 10(13A))</div>
      <div class="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th class="t-left">Factor</th>
              <th class="t-balance">Monthly Value</th>
            </tr>
          </thead>
          <tbody>
            <tr [class.prepay-highlight]="result.limitingFactor === 'actualHra'">
              <td class="t-left">Actual HRA Received</td>
              <td class="t-balance">{{ fmt.currency(result.actualHra) }}</td>
            </tr>
            <tr [class.prepay-highlight]="result.limitingFactor === 'rentMinusTenPercent'">
              <td class="t-left">Rent Paid &minus; 10% of Basic Salary</td>
              <td class="t-balance">{{ fmt.currency(result.rentMinusTenPercent) }}</td>
            </tr>
            <tr [class.prepay-highlight]="result.limitingFactor === 'percentOfSalary'">
              <td class="t-left">{{ isMetro ? '50%' : '40%' }} of Basic Salary ({{ isMetro ? 'Metro' : 'Non-metro' }})</td>
              <td class="t-balance">{{ fmt.currency(result.percentOfSalary) }}</td>
            </tr>
            <tr>
              <td class="t-left"><strong>= Exempt HRA (least of the above)</strong></td>
              <td class="t-balance"><strong>{{ fmt.currency(result.exemptHra) }}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  }
</div>

<!-- ══ SEO CONTENT ══════════════════════════════════════════════════════ -->
<section class="container seo-content">
  <div class="card">
    <h2 class="section-title">How is HRA Exemption Calculated?</h2>
    <p>
      House Rent Allowance (HRA) exemption under Section 10(13A) of the Income Tax Act is available
      only under the Old Tax Regime, to salaried employees who pay rent for their accommodation.
      The exemption is the <strong>least</strong> of three amounts:
    </p>
    <ul class="emi-formula-vars">
      <li>Actual HRA received from your employer</li>
      <li>Rent paid minus 10% of Basic Salary + DA</li>
      <li>50% of Basic Salary + DA (metro cities) or 40% (non-metro cities)</li>
    </ul>
    <p>
      Whatever HRA you receive beyond this exempt amount is added back to your taxable salary.
    </p>

    <h2 class="section-title mt-sm">Frequently Asked Questions</h2>
    <div class="faq-item">
      <h3>Can I claim HRA exemption under the New Tax Regime?</h3>
      <p>
        No. HRA exemption under Section 10(13A) is only available if you opt for the Old Tax
        Regime. The New Regime does not allow this exemption.
      </p>
    </div>
    <div class="faq-item">
      <h3>What counts as a "metro city" for HRA purposes?</h3>
      <p>
        From FY 2026-27 onwards, Delhi, Mumbai, Kolkata, Chennai, Bangalore, Pune, Hyderabad and
        Ahmedabad are classified as metro cities, entitling you to the 50% of salary limit.
        All other cities use the 40% limit.
      </p>
    </div>
    <p class="learn-crosslink">
      Read more: <a routerLink="/learn/hra-exemption-rules-explained">HRA exemption rules explained with examples</a>
    </p>
  </div>
</section>
